/**
 * MIT License
 *
 * Copyright (c) 2019 BottleTech Limited
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import Marzipano from "marzipano";

const defaultResolution = 376;
const defaultFov = (Math.PI * 1) / 2;
const defaultViewParams = { yaw: 0, pitch: 0, roll: 0, defaultFov };
const defaultPitchLimiterParams = { min: -0.3, max: 0.5 };

const loadScene = (viewer) => (sceneSpec) => {
  const {
    imageUrl,
    type,
    initialViewParameters,
    initialPitchLimiter,
    initialYawLimiter,
    faceSize,
  } = sceneSpec;
  const pitchLimiter = initialPitchLimiter || defaultPitchLimiterParams;
  const yawLimiter = initialYawLimiter || false;

  let defaultViewLimiter;

  if (yawLimiter) {
    defaultViewLimiter = Marzipano.util.compose(
      Marzipano.RectilinearView.limit.traditional(
        faceSize,
        (120 * Math.PI) / 180,
        (120 * Math.PI) / 180
      ),
      Marzipano.RectilinearView.limit.pitch(pitchLimiter.min, pitchLimiter.max),
      Marzipano.RectilinearView.limit.yaw(yawLimiter.min, yawLimiter.max)
    );
  } else {
    defaultViewLimiter = Marzipano.util.compose(
      Marzipano.RectilinearView.limit.traditional(
        faceSize,
        (120 * Math.PI) / 180,
        (120 * Math.PI) / 180
      ),
      Marzipano.RectilinearView.limit.pitch(pitchLimiter.min, pitchLimiter.max)
    );
  }

  const defaultLevels = [{ width: defaultResolution }];

  const levels = sceneSpec.levels || defaultLevels;

  const viewParams = initialViewParameters || defaultViewParams;
  const viewLimiter = sceneSpec.viewLimiter || defaultViewLimiter;
  const view = new Marzipano.RectilinearView(viewParams, viewLimiter);

  const geometry =
    type === "equirect"
      ? new Marzipano.EquirectGeometry(levels)
      : new Marzipano.CubeGeometry(levels);
  const source =
    typeof imageUrl === "function"
      ? new Marzipano.ImageUrlSource(imageUrl)
      : Marzipano.ImageUrlSource.fromString(imageUrl);

  return viewer.createScene({ source, geometry, view });
};

const unloadScene = (viewer) => (scene) => {
  viewer.destroyScene(scene);
};

let currentListener = null;

function switchScene(viewer, scene, transitionDuration = 0, onLoad = null) {
  if (viewer && scene) {
    if (onLoad) {
      if (currentListener) {
        viewer.stage().removeEventListener("renderComplete", currentListener);
      }
      currentListener = onLoad;
      viewer.stage().addEventListener("renderComplete", onLoad);
    }

    scene.switchTo({ transitionDuration });
  }
}
export { loadScene, unloadScene, switchScene };
